import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, IconButton, MenuItem, Stack, Typography, TextField } from '@mui/material';
import {
    Edit,
    Visibility,
    PhotoLibraryOutlined,
    AddCircleOutline,
    DeleteOutline,
    Link as LinkIcon,
    StopCircleOutlined,
    LockOutlined,
} from '@mui/icons-material';
import { MaterialReactTable } from 'material-react-table';
import { useListagemPocos } from '../modules/useListagemPocos';
import ToolbarPocos from './ToolbarPocos';
import { getMRTDefaultProps } from '../../../../helpers/mrt/getMRTDefaultProps';
import * as API from '../../../../api/pocos';
import Arquivos from './Arquivos';
import { mensagemErro } from '../modules/valores';
import { printPdf } from '../../../../utils/funcoes';
import { colunas } from './colunasPocos';

const textAlert = (value) => String(value).replaceAll('_', ' ');
function Lacres({ registro }) {
    const [data, setData] = useState(null),
        [erro, setErro] = useState('');
    useEffect(() => {
        const controller = new AbortController();
        API.consultar('lacres/listar', { medidorUuid: registro.uuid }, { signal: controller.signal })
            .then((r) => {
                if (!controller.signal.aborted) setData(r.dados);
            })
            .catch((e) => {
                if (!controller.signal.aborted) setErro(mensagemErro(e));
            });
        return () => controller.abort();
    }, [registro.uuid]);
    if (erro) return <Alert severity="error">{erro}</Alert>;
    if (!data) return <Typography>Carregando lacres…</Typography>;
    return (
        <Box sx={{ p: 1 }}>
            <Typography variant="subtitle1">Histórico de lacres</Typography>
            {!data.length && <Typography>Nenhum lacre cadastrado.</Typography>}
            {data.map((l) => (
                <Typography key={l.uuid}>
                    {l.numero} • instalado: {l.instaladoEm ? new Date(l.instaladoEm).toLocaleString('pt-BR') : '—'} • retirado:{' '}
                    {l.retiradoEm ? new Date(l.retiradoEm).toLocaleString('pt-BR') : 'não'}
                </Typography>
            ))}
        </Box>
    );
}
export default function Lista({ recurso, bem, p, parametro, isMobile, onAction, busy }) {
    const columns = useMemo(() => colunas(recurso), [recurso]);
    const m = useListagemPocos({ recurso, bem, parametro, columns, autorizado: p.autorizado });
    const { mrt, rows, erro, setErro, consulta } = m;
    const [imprimindo, setImprimindo] = useState(false);
    const defaults = getMRTDefaultProps({ mrt, parametro, isMobile });
    async function imprimir() {
        if (!p.imprimir || imprimindo || m.erroPeriodo) return;
        setImprimindo(true);
        setErro('');
        try {
            await printPdf(await API.relatorio(recurso, consulta));
        } catch (e) {
            setErro(mensagemErro(e));
        } finally {
            setImprimindo(false);
        }
    }
    function actions(row) {
        if (recurso === 'medidores')
            return [
                {
                    id: 'lacre',
                    label: 'Instalar / trocar lacre',
                    icon: <LockOutlined />,
                    allowed: p.editar && row.tipo === 'AGUA' && !row.retiradoEm,
                },
                {
                    id: 'encerrar',
                    label: 'Encerrar medidor',
                    icon: <StopCircleOutlined />,
                    allowed: p.cancelar && !row.retiradoEm,
                },
            ];
        return [
            {
                id: 'abrir',
                label: p.editar ? 'Consultar / editar' : 'Consultar',
                icon: p.editar ? <Edit /> : <Visibility />,
                allowed: true,
            },
            ...(recurso === 'outorgas'
                ? [
                      { id: 'versao', label: 'Nova versão', icon: <AddCircleOutline />, allowed: p.incluir },
                      {
                          id: 'excluir',
                          label: 'Excluir rascunho',
                          icon: <DeleteOutline />,
                          allowed: p.cancelar && row.situacao === 'RASCUNHO',
                      },
                  ]
                : [
                      {
                          id: 'arquivos',
                          label: 'Fotos e anexos — visualizar / baixar',
                          icon: <PhotoLibraryOutlined />,
                          allowed: true,
                      },
                      {
                          id: 'regularizar',
                          label: 'Regularizar outorga',
                          icon: <LinkIcon />,
                          allowed: p.editar && row.situacao !== 'CANCELADA',
                      },
                      {
                          id: 'cancelar',
                          label: 'Cancelar medição',
                          icon: <DeleteOutline />,
                          allowed: p.cancelar && row.situacao !== 'CANCELADA',
                      },
                  ]),
        ];
    }
    return (
        <Box sx={{ minWidth: 0 }}>
            {erro && <Alert severity="error">{erro}</Alert>}
            {recurso === 'medicoes' && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                    {['dataInicio', 'dataFim'].map((name) => (
                        <TextField
                            key={name}
                            type="date"
                            size="small"
                            label={name === 'dataInicio' ? 'Data inicial da medição' : 'Data final da medição'}
                            title={
                                name === 'dataInicio'
                                    ? 'Exibe medições a partir desta data, inclusive. Deixe vazio para não limitar o início.'
                                    : 'Exibe medições até esta data, inclusive. Deixe vazio para não limitar o fim.'
                            }
                            value={m.periodo[name]}
                            InputLabelProps={{ shrink: true }}
                            error={Boolean(m.erroPeriodo)}
                            onChange={(e) => m.setPeriodo((prev) => ({ ...prev, [name]: e.target.value }))}
                        />
                    ))}
                    <Button
                        title="Remover somente as datas inicial e final"
                        onClick={() => m.setPeriodo({ dataInicio: '', dataFim: '' })}
                    >
                        Limpar período
                    </Button>
                </Stack>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Clique no cabeçalho para ordenar; Shift + clique combina colunas. Expanda a linha para consultar os detalhes.
            </Typography>
            <MaterialReactTable
                {...defaults}
                columns={columns}
                data={rows}
                getRowId={(r) => r.uuid}
                enableRowSelection={false}
                enableGlobalFilterModes={false}
                enableExpanding
                enableExpandAll={false}
                getRowCanExpand={(row) => recurso !== 'medidores' || row.original.tipo === 'AGUA'}
                enableMultiSort
                maxMultiSortColCount={5}
                muiSearchTextFieldProps={{
                    placeholder:
                        recurso === 'medicoes'
                            ? 'Pesquisar observações'
                            : recurso === 'outorgas'
                              ? 'Portaria, processo ou órgão'
                              : 'Identificação ou série',
                }}
                muiPaginationProps={{
                    ...defaults.muiPaginationProps,
                    rowsPerPageOptions: (defaults.muiPaginationProps?.rowsPerPageOptions || [5, 10, 20, 50, 100, 200]).filter(
                        (v) => Number(v) <= 200
                    ),
                }}
                muiExpandButtonProps={({ row }) => ({
                    title: row.getIsExpanded() ? 'Recolher' : recurso === 'medidores' ? 'Ver lacres' : 'Ver alertas e arquivos',
                })}
                renderTopToolbarCustomActions={() => (
                    <ToolbarPocos
                        isMobile={isMobile}
                        actions={[
                            {
                                key: 'novo',
                                label: 'Novo',
                                title: 'Cadastrar um novo registro neste poço',
                                mobileVisible: true,
                                variant: 'contained',
                                disabled: !p.incluir || busy,
                                onClick: () => onAction('novo'),
                            },
                            {
                                key: 'imprimir',
                                label: imprimindo ? 'Gerando PDF…' : 'Imprimir',
                                title: 'Imprimir todos os resultados com os mesmos filtros, período e ordenação da consulta',
                                mobileVisible: true,
                                disabled: !p.imprimir || imprimindo || busy || Boolean(m.erroPeriodo),
                                onClick: imprimir,
                            },
                            {
                                key: 'atualizar',
                                label: 'Atualizar',
                                title: 'Atualizar mantendo os critérios da consulta',
                                disabled: busy || mrt.isLoading || mrt.isRefetching,
                                onClick: m.carregar,
                            },
                            {
                                key: 'limpar',
                                label: 'Limpar filtros',
                                title: 'Remover filtros, período, pesquisa e ordenação personalizada',
                                disabled: busy,
                                onClick: m.limpar,
                            },
                        ]}
                    />
                )}
                renderRowActions={
                    isMobile
                        ? undefined
                        : ({ row }) => (
                              <Stack direction="row">
                                  {actions(row.original).map((a) => (
                                      <IconButton
                                          key={a.id}
                                          title={a.label}
                                          aria-label={a.label}
                                          disabled={!a.allowed || busy}
                                          onClick={() => onAction(a.id, row.original)}
                                      >
                                          {a.icon}
                                      </IconButton>
                                  ))}
                              </Stack>
                          )
                }
                renderRowActionMenuItems={
                    isMobile
                        ? ({ row, closeMenu }) =>
                              actions(row.original).map((a) => (
                                  <MenuItem
                                      key={a.id}
                                      disabled={!a.allowed || busy}
                                      onClick={() => {
                                          closeMenu();
                                          onAction(a.id, row.original);
                                      }}
                                  >
                                      {a.label}
                                  </MenuItem>
                              ))
                        : undefined
                }
                renderDetailPanel={({ row }) =>
                    !row.getIsExpanded() ? null : recurso === 'medidores' ? (
                        <Lacres registro={row.original} />
                    ) : (
                        <>
                            {(row.original.alertas || []).map((a, i) => (
                                <Alert severity="warning" key={i} sx={{ mb: 1 }}>
                                    {textAlert(a)}
                                </Alert>
                            ))}
                            <Arquivos
                                tipo={recurso}
                                registro={row.original}
                                podeAnexar={p.editar && row.original.situacao !== 'CANCELADA'}
                            />
                        </>
                    )
                }
            />
        </Box>
    );
}
